/**
 * DOTS Coefficient Calculator
 *
 * DOTS (Dynamic Objective Team Scoring) is the official coefficient
 * used by the International Powerlifting Federation (IPF) since 2019.
 * It normalizes strength scores across different bodyweights, making
 * fair comparisons possible between a 60kg lifter and a 120kg lifter.
 *
 * Formula: DOTS = Total × (500 / (a×BW⁴ + b×BW³ + c×BW² + d×BW + e))
 *
 * Where BW is bodyweight in kg and a-e are gender-specific constants.
 */

// Male DOTS coefficients (IPF official)
const MALE_A = -0.0000010930;
const MALE_B = 0.0007391293;
const MALE_C = -0.1918759221;
const MALE_D = 24.0900756;
const MALE_E = -307.75076;

// Female DOTS coefficients (IPF official)
const FEMALE_A = -0.0000010706;
const FEMALE_B = 0.0005158568;
const FEMALE_C = -0.1126655495;
const FEMALE_D = 13.6175032;
const FEMALE_E = -57.96288;

/**
 * Calculate the DOTS denominator for a given bodyweight and gender.
 */
function dotsDenominator(bwKg: number, gender: 'male' | 'female'): number {
  const a = gender === 'male' ? MALE_A : FEMALE_A;
  const b = gender === 'male' ? MALE_B : FEMALE_B;
  const c = gender === 'male' ? MALE_C : FEMALE_C;
  const d = gender === 'male' ? MALE_D : FEMALE_D;
  const e = gender === 'male' ? MALE_E : FEMALE_E;

  const bw2 = bwKg * bwKg;
  const bw3 = bw2 * bwKg;
  const bw4 = bw3 * bwKg;

  return a * bw4 + b * bw3 + c * bw2 + d * bwKg + e;
}

/**
 * Calculate DOTS score.
 *
 * @param totalKg - Total lifted in kg (sum of best lifts, or strength score in kg)
 * @param bodyweightKg - Lifter's bodyweight in kg
 * @param gender - 'male' or 'female'
 * @returns DOTS score (higher = relatively stronger)
 */
export function calculateDOTS(
  totalKg: number,
  bodyweightKg: number,
  gender: 'male' | 'female',
): number {
  if (totalKg <= 0 || bodyweightKg <= 0) return 0;

  // Clamp bodyweight to valid range (40-210kg)
  const bw = Math.max(40, Math.min(210, bodyweightKg));
  const denom = dotsDenominator(bw, gender);

  if (denom <= 0) return 0;

  return Math.round((totalKg * (500 / denom)) * 100) / 100;
}

/**
 * Convert lbs to kg.
 */
export function lbsToKg(lbs: number): number {
  return lbs * 0.453592;
}

/**
 * Calculate DOTS from a strength score in lbs.
 */
export function calculateDOTSFromLbs(
  totalLbs: number,
  bodyweightLbs: number,
  gender: 'male' | 'female',
): number {
  return calculateDOTS(lbsToKg(totalLbs), lbsToKg(bodyweightLbs), gender);
}
